import Color from 'color'
import { useMemo, useRef, useState, useCallback } from 'react'
import { Button } from "@/components/ui/button"
import { Check, Copy } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useTranslations } from 'next-intl'
import { GetStaticProps } from 'next'

import ColorPicker from '../components/color-picker'
import Header from '../components/layouts/header'
import { Seo } from '@/components/seo'

// https://www.rapidtables.com/convert/color/rgb-to-hsl.html
function ColorPage() {
  const t = useTranslations('ColorPage')
  const [color, setColor] = useState(Color('#f6ad55'))
  const [copiedStates, setCopiedStates] = useState({
    hsl: false,
    rgb: false,
    hex: false,
    cmyk: false,
    hwb: false,
    lab: false,
    lch: false,
    xyz: false
  })

  const copyToClipboard = useCallback((text: string, key: keyof typeof copiedStates) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedStates(prev => ({ ...prev, [key]: true }))
      setTimeout(() => setCopiedStates(prev => ({ ...prev, [key]: false })), 2000)
    })
  }, [])

  const colorFormats = [
    { label: t('rgb'), value: color.round().string(), key: 'rgb' },
    { label: t('hex'), value: color.hexa(), key: 'hex' },
    { label: t('hsl'), value: color.hsl().round().string(), key: 'hsl' },
    { label: t('hwb'), value: color.hwb().round().string(), key: 'hwb' },
    { label: t('lab'), value: color.lab().round().string(), key: 'lab' },
    { label: t('lch'), value: color.lch().round().string(), key: 'lch' },
    { label: t('cmyk'), value: `${color.cyan().toFixed(2)}C ${color.magenta().toFixed(2)}M ${color.yellow().toFixed(2)}Y ${color.black().toFixed(2)}K`, key: 'cmyk' },
    { label: t('xyz'), value: color.xyz().round().string(), key: 'xyz' },
  ]

  return (
    <div>
      <Seo
        title={t('pageTitle')}
        description={t('pageDescription')}
        thumbnail="https://devtool.tech/thumbnail/color-convert.png"
        path="/color"
      />
      <div className="container px-4 m-auto md:px-16">
        <Header title={t('headerTitle')} />
      </div>
      <ColorPicker onChange={setColor} color={color} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12 font-mono">
        {colorFormats.map(({ label, value, key }) => (
          <Card key={key} className="w-full max-w-[400px] odd:justify-self-end">
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <div className="text-sm font-semibold">{label}</div>
                <div className="mt-1">{value}</div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(value, key as keyof typeof copiedStates)}
                    >
                      {copiedStates[key as keyof typeof copiedStates] ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{copiedStates[key as keyof typeof copiedStates] ? t('copied') : t('copyToClipboard')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default ColorPage

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}